'use client';

import { lazy, Suspense } from 'react';
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

const DotLottie = lazy(() =>
  import('@lottiefiles/dotlottie-react').then((m) => ({ default: m.DotLottieReact })),
);

const CONSTANTS = {
  itemSize: 48,
  containerSize: 260,
  triggerSize: 150,
  openStagger: 0.02,
  closeStagger: 0.07,
};



const pointOnCircle = (i: number, n: number, r: number, cx = 0, cy = 0) => {
  const theta = (2 * Math.PI * i) / n - Math.PI / 2;
  const x = cx + r * Math.cos(theta);
  const y = cy + r * Math.sin(theta) + 0;
  return { x, y };
};

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  href: string;
  index: number;
  totalItems: number;
  isOpen: boolean;
  isActive: boolean;
  onClick?: () => void;
}

const MenuItem = ({ icon, label, index, totalItems, isOpen, isActive, onClick }: MenuItemProps) => {
  const { x, y } = pointOnCircle(index, totalItems, CONSTANTS.containerSize / 2);
  const [hovering, setHovering] = useState(false);

  return (
    <motion.button
      animate={{
        x: isOpen ? x : 0,
        y: isOpen ? y : 0,
        scale: isActive && isOpen ? 1.15 : 1,
        opacity: isOpen ? 1 : 0,
      }}
      whileHover={{
        scale: 1.1,
        transition: { duration: 0.1, delay: 0 },
      }}
      transition={{
        delay: isOpen ? index * CONSTANTS.openStagger : index * CONSTANTS.closeStagger,
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
      style={{
        height: CONSTANTS.itemSize - 2,
        width: CONSTANTS.itemSize - 2,
        backgroundColor: isActive
          ? 'var(--color-accent, #d4af37)'
          : 'rgba(255,255,255,0.15)',
        color: isActive ? '#1a1a1a' : 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(255,255,255,0.2)',
        backdropFilter: 'blur(8px)',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
      className="rounded-full flex items-center justify-center absolute cursor-pointer"
      onClick={onClick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      aria-label={label}
    >
      {icon}
      {hovering && isOpen && (
        <p
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '6px',
            fontSize: '10px',
            fontFamily: 'var(--font-mono, monospace)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.9)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {label}
        </p>
      )}
    </motion.button>
  );
};

interface MenuTriggerProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  itemsLength: number;
  closeAnimationCallback: () => void;
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
}

const MenuTrigger = ({
  setIsOpen,
  isOpen,
  itemsLength,
  closeAnimationCallback,
  openIcon,
  closeIcon,
}: MenuTriggerProps) => {
  const animate = useAnimationControls();
  const shakeAnimation = useAnimationControls();

  const scaleTransition = Array.from({ length: itemsLength - 1 })
    .map((_, index) => index + 1)
    .reduce((acc, _, index) => {
      const increasedValue = index * 0.15;
      acc.push(1 + increasedValue);
      return acc;
    }, [] as number[]);

  const closeAnimation = async () => {
    shakeAnimation.start({
      translateX: [0, 2, -2, 0, 2, -2, 0],
      transition: {
        duration: CONSTANTS.closeStagger,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
    for (let i = 0; i < scaleTransition.length; i++) {
      await animate.start({
        height: Math.min(
          CONSTANTS.triggerSize * scaleTransition[i],
          CONSTANTS.triggerSize + CONSTANTS.triggerSize / 2,
        ),
        width: Math.min(
          CONSTANTS.triggerSize * scaleTransition[i],
          CONSTANTS.triggerSize + CONSTANTS.triggerSize / 2,
        ),
        transition: {
          duration: CONSTANTS.closeStagger / 2,
          ease: 'linear',
        },
      });
      if (i !== scaleTransition.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, CONSTANTS.closeStagger * 1000));
      }
    }

    shakeAnimation.stop();
    shakeAnimation.start({
      translateX: 0,
      transition: { duration: 0 },
    });

    animate.start({
      height: CONSTANTS.triggerSize,
      width: CONSTANTS.triggerSize,
      transition: {
        duration: 0.1,
        ease: 'backInOut',
      },
    });
  };

  return (
    <motion.div animate={shakeAnimation} className="z-50">
      <motion.button
        animate={animate}
        style={{
          height: CONSTANTS.triggerSize,
          width: CONSTANTS.triggerSize,
          background: 'transparent',
          color: '#fff',
          border: 'none',
          boxShadow: 'none',
          overflow: 'visible',
        }}
        className="rounded-full flex items-center justify-center cursor-pointer outline-none ring-0 transition-all duration-100 z-50"
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            closeAnimationCallback();
            closeAnimation();
          } else {
            setIsOpen(true);
          }
        }}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        <AnimatePresence mode="popLayout">
          {isOpen ? (
            <motion.span
              key="menu-close"
              initial={{ opacity: 0, filter: 'blur(10px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(10px)' }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {closeIcon ?? <X size={18} />}
            </motion.span>
          ) : (
            <motion.span
              key="menu-open"
              initial={{ opacity: 0, filter: 'blur(10px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(10px)' }}
              transition={{ duration: 0.2 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              {openIcon ?? (
                <Suspense fallback={<span style={{ width: '100%', height: '100%' }} />}>
                  <DotLottie
                    src="https://lottie.host/345ece24-2ba4-40a3-9d0e-b97288ddc696/oIxyVweUGW.lottie"
                    loop
                    autoplay
                    style={{
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                      transform: 'translateY(12px)',
                    }}
                  />
                </Suspense>
              )}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </motion.div>
  );
};

interface CircleMenuItem {
  label: string;
  icon: React.ReactNode;
  href: string;
}

interface CircleMenuProps {
  items: CircleMenuItem[];
  activeIndex?: number;
  onItemClick?: (index: number) => void;
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
}

const CircleMenu = ({
  items,
  activeIndex = 0,
  onItemClick,
  openIcon,
  closeIcon = <X size={18} />,
}: CircleMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const animate = useAnimationControls();

  // Auto-close menu when active index changes (after navigation)
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsOpen(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [activeIndex, isOpen]);

  const closeAnimationCallback = async () => {
    await animate.start({
      rotate: -360,
      filter: 'blur(1px)',
      transition: {
        duration: CONSTANTS.closeStagger * (items.length + 2),
        ease: 'linear',
      },
    });
    await animate.start({
      rotate: 0,
      filter: 'blur(0px)',
      transition: { duration: 0 },
    });
  };

  return (
    <div
      style={{
        width: CONSTANTS.containerSize,
        height: CONSTANTS.containerSize,
      }}
      className="relative flex items-center justify-center place-self-center"
    >
      {/* Section-aware chat bubble — visible when menu is closed */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            key="bubble-wrap"
            initial={{ opacity: 0, scale: 0.6, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 8 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20, delay: 0.6 }}
            style={{
              position: 'absolute',
              top: '52px',
              right: '0',
              background: 'rgba(255,255,255,0.04)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.75)',
              fontSize: '0.68rem',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              padding: '6px 14px',
              borderRadius: '100px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 60,
            }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={activeIndex}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.22 }}
                style={{ display: 'block' }}
              >
                {([
                  'psst.. click me 👆',
                  "that's varun alright 🧠 check experience!",
                  'fresh projects, zero bugs* 🤞 (*allegedly)',
                  'kind words from people who survived working with me 🙌',
                  'words words words ✍️ click a blog!',
                  "don't be shy, say hi 📬",
                ] as const)[activeIndex] ?? 'psst.. click me 👆'}
              </motion.span>
            </AnimatePresence>
            {/* Tail pointing right toward the cat */}
            <span style={{
              position: 'absolute',
              top: '50%',
              right: '-6px',
              transform: 'translateY(-50%)',
              width: 0, height: 0,
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              borderLeft: '6px solid rgba(255,255,255,0.1)',
            }} />
          </motion.div>
        )}
      </AnimatePresence>

      <MenuTrigger
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        itemsLength={items.length}
        closeAnimationCallback={closeAnimationCallback}
        openIcon={openIcon}
        closeIcon={closeIcon}
      />
      <motion.div
        animate={animate}
        className={cn('absolute inset-0 z-0 flex items-center justify-center')}
      >
        {items.map((item, index) => (
          <MenuItem
            key={`menu-item-${index}`}
            icon={item.icon}
            label={item.label}
            href={item.href}
            index={index}
            totalItems={items.length}
            isOpen={isOpen}
            isActive={index === activeIndex}
            onClick={() => onItemClick?.(index)}
          />
        ))}
      </motion.div>
    </div>
  );
};

export { CircleMenu };
